import {produce} from "immer";
import {
    DARK_THEME_COLOR,
    DEFAULT_MODEL,
    DEFAULT_THEME,
    DEFAULT_VOICE,
    LIGHT_THEME_COLOR,
    THEME_COLORS,
    DEFAULT_CLAUDE_SONNET_REASONING_EFFORT,
    DEFAULT_PRIMARY_LANGUAGE,
    DEFAULT_STOP_OUTPUT_PHRASE,
    DEFAULT_SEND_MESSAGE_PHRASE,
    DEFAULT_STOP_RECORDING_PHRASE,
    DEFAULT_CLAUDE_OPUS_REASONING_EFFORT
} from "../../Globals.ts";
import {isMobileApp} from "../../Utils/misc/envNameUtils.ts";
import {writeSettings} from "../../Utils/db/users/userSettings/writeSettings.ts";

interface SettingsObj {
    model: string;
    claude_sonnet_reasoning_effort: string;
    claude_opus_reasoning_effort: string;
    autoTheme: boolean;
    theme: 'light' | 'dark';
    customInstructions: string;
    voice: string;
    sendMessagePhrase: string;
    stopOutputPhrase: string;
    stopRecordingPhrase: string;
    primaryLanguage: string;
    secondaryLanguage: string | null;
    isIncognito: boolean;
    enableSearchInternet: boolean;
    enableDeepResearch: boolean;
    enableGenerateImages: boolean;
    enableCreateDocument: boolean;
    enableRunTerminalCommand: boolean;
    enableReviewChanges: boolean;
    mode: string;
    contextMode: string | null;
    apiKeyOnly: boolean;
    scrapeErrors: boolean;
    scrapeWarnings: boolean;
    customModes: {
        [key: string]: {
            name: string;
            description: string;
            instructions: string;
        };
    };
    projectCustomInstructions: {
        [projectName: string]: string;
    };
}

export const settingsSlice = (set: any, get: any) => ({
    settingsObj: {
        model: DEFAULT_MODEL,
        claude_sonnet_reasoning_effort: DEFAULT_CLAUDE_SONNET_REASONING_EFFORT,
        claude_opus_reasoning_effort: DEFAULT_CLAUDE_OPUS_REASONING_EFFORT,
        autoTheme: true,
        theme: DEFAULT_THEME,
        customInstructions: "",
        voice: DEFAULT_VOICE,
        stopOutputPhrase: DEFAULT_STOP_OUTPUT_PHRASE,
        stopRecordingPhrase: DEFAULT_STOP_RECORDING_PHRASE,
        sendMessagePhrase: DEFAULT_SEND_MESSAGE_PHRASE,
        primaryLanguage: DEFAULT_PRIMARY_LANGUAGE,
        secondaryLanguage: null,
        isIncognito: false,
        enableSearchInternet: true,
        enableDeepResearch: true,
        enableGenerateImages: true,
        enableCreateDocument: true,
        enableRunTerminalCommand: true,
        enableReviewChanges: true,
        mode: "agent",
        contextMode: null,
        apiKeyOnly: false,
        scrapeErrors: true,
        scrapeWarnings: true,
        customModes: {},
        projectCustomInstructions: {}
    },
    setSettingsObj: (newSettingsObj: Partial<SettingsObj>) => set(produce((state: any) => {
        if (newSettingsObj.theme) {
            const root = document.documentElement;

            if (newSettingsObj.theme === "dark") root.style.setProperty('--primary-color', '#171717');
            else root.style.setProperty('--primary-color', '#f5f5f5');

            if (isMobileApp(get().environmentObj.environment)) {
                window.ReactNativeWebView.postMessage(JSON.stringify({
                    functionName: "themeChange",
                    data: [{ theme: newSettingsObj.theme, color: THEME_COLORS }]
                }));
            } else if (get().environmentObj.environment === "chrome") {
                window.parent.postMessage({
                        functionName: 'themeChange',
                        data: [{ theme: newSettingsObj.theme }]
                    },
                    '*');
            }
            document.body.className = newSettingsObj.theme === "dark" ? DARK_THEME_COLOR : LIGHT_THEME_COLOR;
        }

        if (newSettingsObj.model) {
            writeSettings(get().userObj.user, {defaultModel: newSettingsObj.model}, get().environmentObj.environment);
        }

        if (newSettingsObj.contextMode) {
            if (get().environmentObj.environment === "chrome") {
                window.parent.postMessage({
                    functionName: "setContextMode",
                    data: [{contextMode: newSettingsObj.contextMode}]
                }, '*');
            }
        }

        Object.assign(state.settingsObj, newSettingsObj);
    }))
});
